
  const DAY = 86400000;
  const parse = (s) => {
    const [y, m, d] = s.split("-").map(Number);
    return Date.UTC(y, m - 1, d);
  };
  const iso = (t) => new Date(t).toISOString().slice(0, 10);
  const fmt = (t) =>
    new Date(t).toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    });
  const monthStart = (t) => {
    const d = new Date(t);
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1);
  };
  const BUTTON =
    "rounded-md px-2 py-1 text-xs bg-zulti-surface-raised text-zulti-text-secondary hover:text-zulti-text-primary";

  export default {
    mounted() {
      this.q = (role) => this.el.querySelector(`[data-role=${role}]`);
      this.q("trigger").addEventListener("click", () => {
        this.q("panel").classList.toggle("hidden");
        this.pending = null;
        this.render();
      });
      this.el.addEventListener("click", (e) => this.onClick(e));
      // The path is fixed at dispatch, before render() detaches the clicked button.
      this.onOutside = (e) => {
        if (!e.composedPath().includes(this.el)) this.close();
      };
      this.onKey = (e) => {
        if (e.key === "Escape") this.close();
      };
      document.addEventListener("click", this.onOutside);
      document.addEventListener("keydown", this.onKey);
      this.sync();
    },

    updated() {
      this.sync();
    },

    destroyed() {
      document.removeEventListener("click", this.onOutside);
      document.removeEventListener("keydown", this.onKey);
    },

    sync() {
      const d = this.el.dataset;
      this.start = parse(d.start);
      this.end = parse(d.end);
      this.today = parse(d.today);
      this.maxDays = d.maxDays ? Number(d.maxDays) : null;
      this.q("start").value = d.start;
      this.q("end").value = d.end;
      this.pending = null;
      this.month = monthStart(this.end);
      this.render();
    },

    close() {
      this.q("panel").classList.add("hidden");
      this.pending = null;
      this.render();
    },

    presets() {
      const t = this.today;
      const yearStart = Date.UTC(new Date(t).getUTCFullYear(), 0, 1);

      return [
        ["Today", t, t],
        ["Last week", t - 6 * DAY, t],
        ["Last 30 days", t - 29 * DAY, t],
        ["Last 3 months", t - 89 * DAY, t],
        ["Year to date", yearStart, t],
      ].filter(([, s, e]) => !this.maxDays || (e - s) / DAY < this.maxDays);
    },

    selectable(t) {
      if (t > this.today) return false;
      if (this.pending === null || !this.maxDays) return true;
      return Math.abs(t - this.pending) / DAY < this.maxDays;
    },

    onClick(e) {
      const button = e.target.closest(
        "button[data-day], button[data-nav], button[data-preset-start]",
      );
      if (!button || button.disabled) return;
      const { day, nav, presetStart, presetEnd } = button.dataset;

      if (nav) {
        const d = new Date(this.month);
        this.month = Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + Number(nav), 1);
        this.render();
      } else if (presetStart) {
        this.commit(Number(presetStart), Number(presetEnd));
      } else if (this.pending === null) {
        this.pending = Number(day);
        this.render();
      } else {
        const t = Number(day);
        this.commit(Math.min(this.pending, t), Math.max(this.pending, t));
      }
    },

    commit(start, end) {
      this.start = start;
      this.end = end;
      this.q("start").value = iso(start);
      this.q("end").value = iso(end);
      this.close();
      this.el.closest("form").requestSubmit();
    },

    render() {
      this.q("label").textContent =
        this.start === this.end ? fmt(this.start) : `${fmt(this.start)} – ${fmt(this.end)}`;

      this.q("presets").innerHTML = this.presets()
        .map(([label, s, e]) => {
          const active = s === this.start && e === this.end ? " ring-1 ring-zulti-brand-500" : "";
          return `<button type="button" class="${BUTTON}${active}" data-preset-start="${s}" data-preset-end="${e}">${label}</button>`;
        })
        .join("");

      const d = new Date(this.month);
      const days = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
      const lead = (d.getUTCDay() + 6) % 7;
      const [lo, hi] =
        this.pending !== null ? [this.pending, this.pending] : [this.start, this.end];

      let cells = "<span></span>".repeat(lead);
      for (let i = 0; i < days; i++) {
        const t = this.month + i * DAY;
        const selected = t >= lo && t <= hi ? " bg-zulti-brand-500 text-white" : " hover:bg-zulti-surface-raised";
        const disabled = this.selectable(t) ? "" : " disabled";
        cells += `<button type="button" data-day="${t}"${disabled} class="rounded py-1 text-xs disabled:opacity-30${selected}">${i + 1}</button>`;
      }

      const title = d.toLocaleDateString(undefined, {
        month: "long",
        year: "numeric",
        timeZone: "UTC",
      });
      const hint = this.pending !== null ? "Pick the end date" : "Pick the start date";

      this.q("calendar").innerHTML = `
        <div class="mb-2 flex items-center justify-between text-sm text-zulti-text-primary">
          <button type="button" data-nav="-1" class="${BUTTON}" aria-label="Previous month">‹</button>
          <span>${title}</span>
          <button type="button" data-nav="1" class="${BUTTON}" aria-label="Next month">›</button>
        </div>
        <div class="grid grid-cols-7 gap-1 text-center text-[10px] text-zulti-text-secondary">
          ${["M", "T", "W", "T", "F", "S", "S"].map((x) => `<span>${x}</span>`).join("")}
        </div>
        <div class="mt-1 grid grid-cols-7 gap-1 text-center text-zulti-text-primary">${cells}</div>
        <p class="mt-2 text-center text-[10px] text-zulti-text-secondary">${hint}</p>`;
    },
  };
